import {
	type Budget,
	type Category,
	db,
	type IncomeStream,
	type MosDB,
	newId,
	type Transaction,
	type Wallet,
	type WalletType,
} from "./db";
import { assertUGX } from "./money";
import { nextUpdatedAt, remove, save, update } from "./store";

// Finance writes: Dexie + outbox first, like the planner (D34). Amounts are
// whole UGX; these functions refuse anything else rather than round. They
// are async so a validation error arrives as a rejected promise.

export class MoneyError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "MoneyError";
	}
}

function checkAmounts(amount: number, fee: number) {
	assertUGX(amount);
	assertUGX(fee);
	if (amount <= 0) throw new MoneyError("Amount must be more than zero");
	if (fee < 0) throw new MoneyError("Fee cannot be negative");
}

function checkShape(t: Pick<Transaction, "kind" | "wallet_id" | "to_wallet_id" | "stream_id">) {
	if (t.kind === "transfer") {
		if (!t.to_wallet_id) throw new MoneyError("Pick the wallet to move money to");
		if (t.to_wallet_id === t.wallet_id) throw new MoneyError("Pick two different wallets");
	} else if (t.to_wallet_id) {
		throw new MoneyError("Only transfers have a destination wallet");
	}
	if (t.stream_id && t.kind !== "income") throw new MoneyError("Only income has a stream");
}

export type NewTransaction = Pick<Transaction, "kind" | "amount" | "wallet_id"> &
	Partial<Omit<Transaction, "id" | "created_at" | "updated_at" | "deleted_at">>;

export async function createTransaction(
	input: NewTransaction,
	database: MosDB = db,
): Promise<Transaction> {
	const now = nextUpdatedAt();
	const row: Transaction = {
		id: newId(),
		fee: 0,
		occurred_at: new Date().toISOString(),
		source: "manual",
		to_wallet_id: null,
		category_id: null,
		stream_id: null,
		task_id: null,
		...input,
		note: (input.note ?? "").trim(),
		created_at: now,
		updated_at: now,
		deleted_at: null,
	};
	if (row.kind === "transfer") row.category_id = null;
	checkAmounts(row.amount, row.fee);
	checkShape(row);
	return save("transactions", row, database);
}

export async function updateTransaction(
	id: string,
	patch: Partial<Transaction>,
	database: MosDB = db,
): Promise<Transaction> {
	const current = await database.transactions.get(id);
	if (current) {
		const next = { ...current, ...patch };
		checkAmounts(next.amount, next.fee);
		checkShape(next);
	}
	return update("transactions", id, patch, database);
}

export function deleteTransaction(id: string, database: MosDB = db): Promise<void> {
	return remove("transactions", id, database);
}

/** Moves money between wallets; any fee is charged to the source wallet (FN-5, D40). */
export async function createTransfer(
	input: {
		from: string;
		to: string;
		amount: number;
		fee?: number;
		note?: string;
		occurred_at?: string;
	},
	database: MosDB = db,
): Promise<Transaction> {
	return createTransaction(
		{
			kind: "transfer",
			wallet_id: input.from,
			to_wallet_id: input.to,
			amount: input.amount,
			fee: input.fee ?? 0,
			note: input.note,
			occurred_at: input.occurred_at,
		},
		database,
	);
}

export async function createWallet(
	input: { name: string; type: WalletType; opening_balance?: number },
	database: MosDB = db,
): Promise<Wallet> {
	const opening = input.opening_balance ?? 0;
	assertUGX(opening);
	const now = nextUpdatedAt();
	return save(
		"wallets",
		{
			id: newId(),
			name: input.name.trim(),
			type: input.type,
			opening_balance: opening,
			archived: false,
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
		database,
	);
}

export async function updateWallet(
	id: string,
	patch: Partial<Pick<Wallet, "name" | "type" | "opening_balance" | "archived">>,
	database: MosDB = db,
): Promise<Wallet> {
	if (patch.opening_balance !== undefined) assertUGX(patch.opening_balance);
	return update("wallets", id, patch, database);
}

export async function createStream(name: string, database: MosDB = db): Promise<IncomeStream> {
	const now = nextUpdatedAt();
	return save(
		"income_streams",
		{
			id: newId(),
			name: name.trim(),
			archived: false,
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
		database,
	);
}

export function updateStream(
	id: string,
	patch: Partial<Pick<IncomeStream, "name" | "archived">>,
	database: MosDB = db,
): Promise<IncomeStream> {
	return update("income_streams", id, patch, database);
}

export async function createCategory(
	input: { name: string; kind: Category["kind"]; spendable?: boolean },
	database: MosDB = db,
): Promise<Category> {
	const now = nextUpdatedAt();
	return save(
		"categories",
		{
			id: newId(),
			name: input.name.trim(),
			kind: input.kind,
			spendable: input.spendable ?? input.kind === "expense",
			parent_id: null,
			archived: false,
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
		database,
	);
}

/**
 * Sets a category's budget for a month (YYYY-MM-01). One budget per category
 * and month: an existing one is updated, and 0 removes it.
 */
export async function setBudget(
	categoryId: string,
	month: string,
	amount: number,
	database: MosDB = db,
): Promise<Budget | null> {
	assertUGX(amount);
	if (amount < 0) throw new MoneyError("Budget cannot be negative");
	if (!/^\d{4}-\d{2}-01$/.test(month)) throw new MoneyError("Month must be the first day");
	return database.transaction("rw", database.budgets, database.outbox, async () => {
		const existing = (await database.budgets.where("month").equals(month).toArray()).find(
			(b) => b.category_id === categoryId && !b.deleted_at,
		);
		if (amount === 0) {
			if (existing) await remove("budgets", existing.id, database);
			return null;
		}
		if (existing) return update("budgets", existing.id, { amount }, database);
		const now = nextUpdatedAt();
		return save(
			"budgets",
			{
				id: newId(),
				month,
				category_id: categoryId,
				amount,
				created_at: now,
				updated_at: now,
				deleted_at: null,
			},
			database,
		);
	});
}

/** Rename, archive or set whether a category counts toward the allowance. */
export async function updateCategory(
	id: string,
	patch: Partial<Pick<Category, "name" | "spendable" | "archived">>,
	database: MosDB = db,
): Promise<Category> {
	return update("categories", id, patch, database);
}
